// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SnackbarProvider } from 'notistack';
import React from 'react';
import {
  Outlet,
  RouteObject,
  RouterProvider,
  createBrowserRouter,
  Navigate,
} from 'react-router-dom';
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import UtilitiesSnackbar from './components/UtilitiesSnackbar';
import { JSON_API_URL, VALIDATOR_API_URL } from './config/externalApis';
import { useBranding } from './contexts/BrandingContext';
import { JsonApiProvider } from './contexts/JsonApiProvider';
import { LedgerApiClientProvider } from './contexts/LedgerApiProvider';
import { useLocalStorage } from './contexts/LocalStorageContext';
import { LocalStorageProvider } from './contexts/LocalStorageProvider';
import { ModuleProvider } from './contexts/ModuleProvider';
import { PartiesProvider } from './contexts/PartiesProvider';
import { UserProvider } from './contexts/UserProvider';
import { useConfig } from './hooks/other/useConfig';
import { Header } from './layout/Header';
import { Bridge } from './modules/Bridge';
import AuthProvider from './pages/AuthProvider';
import Login from './pages/Login';
import { NotFound } from './pages/NotFound';
import { AppSettings } from './pages/AppSettings';
import { AllSettings } from './pages/settings/AllSettings';
import AuthCheck from './routes/AuthCheck';
import queryClient from './utils/QueryClient';
import { ScanProxyApiProvider } from './contexts/ScanProxyApiProvider';
import { UtilityProxyApiProvider } from './contexts/UtilityApiProvider';

const CommonProviders: React.FC = () => {
  const config = useConfig();
  const { localStorageState } = useLocalStorage();
  const { pollingEnabled } = localStorageState;
  const utilityBackendUrl = config.utilityBackendUrl;

  return (
    <AuthProvider auth={config.auth}>
      <QueryClientProvider client={queryClient(pollingEnabled)}>
        <ReactQueryDevtools initialIsOpen={false} />
        <UserProvider auth={config.auth}>
          <SnackbarProvider
            Components={{
              utilities: UtilitiesSnackbar,
            }}
          >
            <UtilityProxyApiProvider utilityOperatorUrl={utilityBackendUrl}>
              <JsonApiProvider jsonApiUrl={JSON_API_URL}>
                <ScanProxyApiProvider validatorUrl={VALIDATOR_API_URL}>
                  <Outlet />
                </ScanProxyApiProvider>
              </JsonApiProvider>
            </UtilityProxyApiProvider>
          </SnackbarProvider>
        </UserProvider>
      </QueryClientProvider>
    </AuthProvider>
  );
};

const utilityRoutes: RouteObject[] = [
  { path: 'bridge/*', element: <Bridge /> }, // has topbar, has sidebar
  {
    path: 'settings',
    element: <AppSettings />,
    children: [{ path: '', element: <AllSettings /> }],
  },
];

export const App: React.FC = () => {
  const { theme } = useBranding();

  const appRoutes = utilityRoutes;
  const sidebarLayout = <Outlet />;
  const headerLayout = (
    <PartiesProvider>
      <ModuleProvider>
        <LedgerApiClientProvider>
          <Header />
        </LedgerApiClientProvider>
      </ModuleProvider>
    </PartiesProvider>
  );

  const routes: RouteObject = {
    element: <CommonProviders />,
    errorElement: <NotFound />,
    children: [
      { path: 'login', element: <Login /> },
      {
        element: <AuthCheck />,
        children: [
          {
            element: headerLayout,
            children: [
              { index: true, element: <Navigate to="bridge/onboarding" replace /> },
              { element: sidebarLayout, children: appRoutes },
            ],
          },
        ],
      },
    ],
  };

  return (
    <LocalStorageProvider>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <RouterProvider
            future={{ v7_startTransition: true }}
            router={createBrowserRouter([routes], {
              future: {
                // Disable this for now because the self-hosted modules use splat paths, which break under the new v7 behavior.
                v7_relativeSplatPath: false,
              },
            })}
          />
        </ThemeProvider>
      </StyledEngineProvider>
    </LocalStorageProvider>
  );
};
